/*
 * ESPACE PARC — préparer un Master à distribuer un firmware de cible
 *
 * Un seul geste : « Préparer le Master ». Tout s'enchaîne ensuite sans rien
 * demander de plus.
 *
 *   1. on prend le port, on regarde ce que le Master a dans le ventre ;
 *   2. s'il n'est pas à la dernière version, ON LE MET À JOUR — pas de message
 *      « allez d'abord faire l'étape 1 ». Un Master qui arrive ici repart à
 *      jour, c'est tout ;
 *   3. on lui demande quelle image de cible il embarque ;
 *   4. on affiche ce qui est publié, et le bouton de chargement ;
 *   5. on liste les cibles qu'il voit, avec leur version.
 *
 * La mise à jour du Master passe par le bootloader ROM (esptool-js), donc sans
 * coopération du firmware en place : elle fonctionne même sur un Master trop
 * ancien pour répondre, ou dont le firmware est en vrac. C'est précisément ce
 * qui permet de ne jamais bloquer.
 *
 * À noter pour l'étape 5 : la version de chaque cible est déjà dans le nom BLE
 * qu'elle annonce (BZ_<version>_<mode><nom>), et le Master le mémorise. Il n'y a
 * donc personne à interroger pour savoir qui est à jour.
 */

import React, { useState, useRef, useEffect } from "react";
import {
  IonContent,
  IonButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonCardSubtitle,
  IonText,
  IonGrid,
  IonRow,
  IonCol,
  IonList,
  IonItem,
  IonLabel,
  IonBadge,
} from "@ionic/react";
import ProgressBar from "@ramonak/react-progress-bar";
import { Transport, IEspLoaderTerminal } from "esptool-js";
import { ouvrirChargeur } from "./chargeurEsp";
import { demanderPortBzook } from "../portSerie";

// Dossiers où deploy.js dépose les firmware_X.Y.bin de chaque projet
const URL_BINAIRES_CIBLE = "https://ota.bzook.fr/binaires/BZBOX/";
const URL_BINAIRES_MASTER = "https://ota.bzook.fr/binaires/MASTER/";

// Seule l'application est réécrite : bootloader (0x1000) et table de partitions
// (0x8000) sont identiques sur tout le parc. Surtout, ne pas toucher à la table
// reviendrait à préserver la partition de stockage — mais l'application, elle,
// est bien remplacée.
const ADRESSE_APPLICATION = 0x10000;


// Débit des Masters à partir du firmware 0.7. Les 0.6 et antérieurs parlent à
// 9600 mais ne connaissent aucune des commandes utilisées ici : on les détecte
// pour pouvoir le dire clairement, pas pour s'en servir.
const BAUD_MASTER = 115200;
const BAUD_ANCIEN = 9600;

// Web Serial ouvre avec 255 octets par défaut. À 115200, il faut vider le
// tampon toutes les 700 µs pour ne rien perdre : sans marge, les lignes JSON
// ressortent tronquées.
const TAILLE_TAMPON_LECTURE = 4096;

// Découpe par défaut si le Master n'annonce pas la sienne.
const TAILLE_BLOC_DEFAUT = 1536;

// Première version de cible capable de recevoir une mise à jour par ESP-NOW.
// Doit rester alignée sur VERSION_MIN_OTA_ESPNOW dans BzookStruct.h. Une cible
// plus ancienne ignore les messages OTA : son bouton reste donc inactif, elle
// doit passer une dernière fois par le BLE.
const VERSION_MIN_OTA_ESPNOW = "5.14";

// Transfert de ~5 500 blocs acquittés un par un : compter une minute, davantage
// si des blocs se perdent et sont renvoyés.
const DELAI_FLASH_CIBLE = 180000;

/* ------------------------------------------------------------------ outils */

// CRC-32 (polynôme 0xEDB88320). Doit rester identique à StockFirmware::crc32
// côté Master, sinon aucun transfert ne sera jamais déclaré valide.
const crc32 = (donnees: Uint8Array): number => {
  let crc = -1;
  for (let i = 0; i < donnees.length; i++) {
    crc ^= donnees[i];
    for (let k = 0; k < 8; k++) {
      crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1));
    }
  }
  return (crc ^ -1) >>> 0;
};

/**
 * Compresse en zlib avant l'envoi au Master.
 *
 * Indispensable, pas optionnel : deploy.js publie le firmware BRUT
 * (1 742 144 octets pour la cible 5.12), alors que la partition de stockage du
 * Master n'offre que ~1 400 832 octets utiles. Sans compression, ça ne rentre
 * pas — c'est l'échec « image de 1742144 octets pour 1400832 octets libres ».
 *
 * Compressé, on tombe à ~1 081 000 octets : ça passe avec ~300 Ko de marge, le
 * transfert série dure 40 % de moins, et la future diffusion ESP-NOW aussi.
 *
 * Le format doit être du zlib (RFC 1950, avec en-tête), et non du deflate brut :
 * c'est ce que la cible attend côté flashz, qui inflate avec
 * TINFL_FLAG_PARSE_ZLIB_HEADER. D'où "deflate" et non "deflate-raw".
 *
 * Accès dynamique à CompressionStream : l'API n'est pas déclarée dans les
 * anciennes définitions DOM de TypeScript, et son absence doit être une erreur
 * explicite plutôt qu'un échec de compilation.
 */
const compresserZlib = async (donnees: Uint8Array): Promise<Uint8Array> => {
  const Compresseur = (window as any).CompressionStream;
  if (!Compresseur) {
    throw new Error(
      "Ce navigateur ne sait pas compresser (CompressionStream absent). " +
        "Utilisez Chrome ou Edge à jour."
    );
  }
  const flux = (new Blob([donnees]).stream() as any).pipeThrough(
    new Compresseur("deflate")
  );
  return new Uint8Array(await new Response(flux).arrayBuffer());
};

// Un CRC se lit en hexadécimal : deux valeurs proches en décimal peuvent
// différer d'un seul bit, ce qui ne se voit pas.
const enHexa = (n: number): string =>
  "0x" + (n >>> 0).toString(16).toUpperCase().padStart(8, "0");

const enBase64 = (bloc: Uint8Array): string => {
  let s = "";
  for (let i = 0; i < bloc.length; i++) {
    s += String.fromCharCode(bloc[i]);
  }
  return btoa(s);
};

// "firmware_5.13.bin" -> "5.13"
const extraireVersion = (s?: string | null): string | null => {
  if (!s) return null;
  const m = s.match(/(\d+(?:\.\d+)+)/);
  return m ? m[1] : null;
};

// Nom BLE d'une cible : "BZ_5.13_{p8}Dossard12" -> "5.13"
const versionDeCible = (nom?: string | null): string | null => {
  if (!nom) return null;
  const m = nom.match(/^BZ_(\d+(?:\.\d+)+)_/);
  return m ? m[1] : extraireVersion(nom);
};

// Comparaison numérique : "5.9" est antérieure à "5.13", ce qu'un tri
// alphabétique inverserait.
const comparerVersions = (a: string, b: string): number => {
  const pa = a.split(".").map((n) => parseInt(n, 10) || 0);
  const pb = b.split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
};

const trierParVersion = (fichiers: string[]): string[] =>
  [...fichiers].sort((a, b) =>
    comparerVersions(extraireVersion(b) ?? "0", extraireVersion(a) ?? "0")
  );

/* ------------------------------------------------- liaison série au Master */

/**
 * Lit le port en continu et distribue les lignes JSON aux abonnés.
 *
 * Le Master parle en lignes JSON terminées par \r\n, y compris pour ses
 * résultats de course. On ignore simplement ce qui ne concerne pas l'échange en
 * cours plutôt que d'essayer de le faire taire.
 */
class LiaisonMaster {
  private port: SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private tampon = "";
  private abonnes: ((o: any) => void)[] = [];
  private chaineEcriture: Promise<void> = Promise.resolve();
  private arret = false;

  public baud = 0;

  /** Maj + clic sur « Préparer le Master » : lève le filtre sur les puces USB. */
  public sansFiltre = false;

  /** Le port brut, à confier à esptool-js le temps d'un flash. */
  get portBrut(): SerialPort | null {
    return this.port;
  }

  /** Reprend un port déjà obtenu : requestPort() exige un geste utilisateur, on
   *  ne le redemande donc pas entre deux phases d'une même séquence. */
  adopter(port: SerialPort) {
    this.port = port;
  }

  /**
   * Idempotent : appeler ouvrir() sur un port déjà ouvert au bon débit ne fait
   * rien, au lieu de lever « The port is already open ».
   *
   * Sans cette garantie, chaque enchaînement d'étapes devait savoir si la
   * précédente avait laissé le port ouvert ou non — une connaissance qui se
   * perd au premier refactoring, et c'est exactement ce qui est arrivé.
   */
  async ouvrir(baud: number) {
    if (!this.port) {
      this.port = await demanderPortBzook(this.sansFiltre);
    }

    if (this.port.readable && this.baud === baud) {
      // Déjà ouvert comme il faut. On s'assure seulement que la boucle de
      // lecture tourne : elle a pu être arrêtée par un fermerFlux().
      if (!this.promesseLecture) {
        this.arret = false;
        this.promesseLecture = this.boucleLecture();
      }
      return;
    }

    // Ouvert à un autre débit : refermer proprement avant de rouvrir.
    if (this.port.readable || this.port.writable) {
      await this.fermerFlux();
      try {
        await this.port.close();
      } catch {
        /* déjà fermé entre-temps */
      }
    }

    await this.port.open({
      baudRate: baud,
      bufferSize: TAILLE_TAMPON_LECTURE,
      parity: "none",
      stopBits: 1,
    });
    this.baud = baud;
    this.arret = false;
    // La promesse est conservée : c'est le SEUL moyen de savoir quand la boucle
    // a réellement rendu son verrou sur port.readable. Attendre « 60 ms au
    // jugé » laissait parfois le verrou pris, port.close() échouait en
    // silence, et tout ce qui suivait se bloquait sans recours.
    this.promesseLecture = this.boucleLecture();
  }

  private promesseLecture: Promise<void> | null = null;

  private async boucleLecture() {
    if (!this.port?.readable) return;
    this.reader = this.port.readable.getReader();
    const decodeur = new TextDecoder();
    try {
      while (!this.arret) {
        const { value, done } = await this.reader.read();
        if (done) break;
        if (!value) continue;
        this.tampon += decodeur.decode(value, { stream: true });
        const lignes = this.tampon.split("\r\n");
        this.tampon = lignes.pop() ?? "";
        for (const l of lignes) {
          const t = l.trim();
          if (!t.startsWith("{") || !t.endsWith("}")) continue;
          try {
            const o = JSON.parse(t);
            this.abonnes.forEach((f) => f(o));
          } catch {
            /* ligne illisible : au mauvais débit, c'est attendu */
          }
        }
        // Au mauvais débit on reçoit du bruit sans fin de ligne : borner le
        // tampon évite de le laisser gonfler indéfiniment.
        if (this.tampon.length > 8192) this.tampon = this.tampon.slice(-1024);
      }
    } catch {
      /* flux annulé ou octets incohérents */
    }
    try {
      this.reader?.releaseLock();
    } catch {
      /* déjà libéré */
    }
    this.reader = null;
  }

  /** Écritures sérialisées : deux getWriter() concurrents échouent. */
  async envoyer(ligne: string) {
    this.chaineEcriture = this.chaineEcriture
      .then(async () => {
        if (!this.port?.writable) return;
        const writer = this.port.writable.getWriter();
        try {
          await writer.write(new TextEncoder().encode(ligne + "\n"));
        } finally {
          writer.releaseLock();
        }
      })
      .catch(() => {
        /* port fermé entre-temps */
      });
    await this.chaineEcriture;
  }

  /** Abonnement durable, pour suivre un flux de progression. */
  abonner(fn: (o: any) => void) {
    this.abonnes.push(fn);
  }
  desabonner(fn: (o: any) => void) {
    this.abonnes = this.abonnes.filter((f) => f !== fn);
  }

  /** Attend la première réponse qui satisfait le prédicat. */
  attendre(predicat: (o: any) => boolean, delaiMs: number): Promise<any> {
    return new Promise((resolve, reject) => {
      const minuteur = setTimeout(() => {
        desabonner();
        reject(new Error("pas de réponse du Master"));
      }, delaiMs);
      const abonne = (o: any) => {
        if (!predicat(o)) return;
        clearTimeout(minuteur);
        desabonner();
        resolve(o);
      };
      const desabonner = () => {
        this.abonnes = this.abonnes.filter((f) => f !== abonne);
      };
      this.abonnes.push(abonne);
    });
  }

  /** Collecte pendant une fenêtre de temps, sans attendre de réponse unique. */
  collecter(predicat: (o: any) => boolean, fin: (o: any) => boolean, delaiMs: number): Promise<any[]> {
    return new Promise((resolve) => {
      const recus: any[] = [];
      const minuteur = setTimeout(() => {
        desabonner();
        resolve(recus);
      }, delaiMs);
      const abonne = (o: any) => {
        if (predicat(o)) recus.push(o);
        if (fin(o)) {
          clearTimeout(minuteur);
          desabonner();
          resolve(recus);
        }
      };
      const desabonner = () => {
        this.abonnes = this.abonnes.filter((f) => f !== abonne);
      };
      this.abonnes.push(abonne);
    });
  }

  async fermerFlux() {
    this.arret = true;
    try {
      await this.reader?.cancel();
    } catch {
      /* déjà annulé */
    }
    // On attend la fin RÉELLE de la boucle, qui rend le verrou dans son bloc
    // final. Sans ça, port.close() peut échouer parce que port.readable est
    // encore verrouillé — et l'échec est invisible.
    if (this.promesseLecture) {
      await this.promesseLecture;
      this.promesseLecture = null;
    }
  }

  /**
   * Ferme le port et VÉRIFIE qu'il l'est. Lève si ce n'est pas le cas :
   * remettre un port encore ouvert à esptool-js, ou le rouvrir alors qu'il
   * l'est déjà, produit des erreurs incompréhensibles bien plus loin.
   */
  async fermer() {
    await this.fermerFlux();
    if (this.port && (this.port.readable || this.port.writable)) {
      await this.port.close();
    }
  }

  async refermerPourAutreDebit() {
    await this.fermerFlux();
    try {
      await this.port?.close();
    } catch {
      /* déjà fermé */
    }
  }

  /** Oublie le port sans y toucher : utilisé quand on repart de zéro. */
  liberer() {
    this.port = null;
    this.reader = null;
    this.promesseLecture = null;
  }
}

/* --------------------------------------------------------------- composant */

type EtatChargement = "repos" | "encours" | "ok" | "erreur";

interface Cible {
  mac: string;
  nom: string;
}

const ParcCibles: React.FC<{ onFermer: () => void }> = ({ onFermer }) => {
  const [connecte, setConnecte] = useState(false);
  const [versionMaster, setVersionMaster] = useState<string | null>(null);
  // MAC du Master : c'est elle qu'on note pour savoir quelles cibles
  // travaillent avec quel Master. Le Master ne l'annonce qu'au boot et en
  // réponse à PING, et rien ne l'affichait — il fallait aller la lire dans la
  // console du navigateur.
  const [macMaster, setMacMaster] = useState<string | null>(null);
  const [masterMisAJour, setMasterMisAJour] = useState(false);
  // Le Master a bien été flashé mais la reconnexion a échoué : on doit proposer
  // de reprendre à l'étape suivante, surtout pas de tout recommencer.
  const [etapeReprise, setEtapeReprise] = useState(false);
  const [fichiersMaster, setFichiersMaster] = useState<string[]>([]);
  const [stockVersion, setStockVersion] = useState<string | null>(null);
  const [stockTaille, setStockTaille] = useState<number>(0);
  const [placeLibre, setPlaceLibre] = useState<number>(0);
  const [tailleBloc, setTailleBloc] = useState<number>(TAILLE_BLOC_DEFAUT);

  const [fichiers, setFichiers] = useState<string[]>([]);
  const [erreurWeb, setErreurWeb] = useState<string | null>(null);

  const [cibles, setCibles] = useState<Cible[]>([]);

  const [etat, setEtat] = useState<EtatChargement>("repos");
  const [phase, setPhase] = useState<
    "repos" | "preparation" | "chargement" | "flashCible"
  >("repos");
  const [message, setMessage] = useState("");
  const [journal, setJournal] = useState<string[]>([]);

  // Deux opérations distinctes, donc deux avancements suivis séparément — avec
  // de part et d'autre la version d'où l'on part et celle où l'on va. Une seule
  // barre partagée aurait redémarré à zéro au milieu sans qu'on sache pourquoi.
  const [progMaster, setProgMaster] = useState(0);
  const [masterAvant, setMasterAvant] = useState<string | null>(null);
  const [masterVers, setMasterVers] = useState<string | null>(null);
  const [progImage, setProgImage] = useState(0);
  const [imageAvant, setImageAvant] = useState<string | null>(null);
  const [imageVers, setImageVers] = useState<string | null>(null);

  // Les deux empreintes, affichées côte à côte en fin de transfert.
  const [crcLocal, setCrcLocal] = useState<number | null>(null);
  const [crcMaster, setCrcMaster] = useState<number | null>(null);

  // Mise à jour d'une cible par ESP-NOW : une à la fois.
  const [cibleEnCours, setCibleEnCours] = useState<string | null>(null);
  const [progCible, setProgCible] = useState(0);

  const liaison = useRef<LiaisonMaster | null>(null);
  const finJournal = useRef<HTMLDivElement | null>(null);

  const serieDisponible =
    typeof navigator !== "undefined" && "serial" in navigator;

  const journaliser = (l: string) =>
    setJournal((j) => [...j, l].slice(-400));

  useEffect(() => {
    finJournal.current?.scrollIntoView({ block: "end" });
  }, [journal]);

  // Ce qui est publié sur le serveur, pour les deux projets. Fait au montage :
  // savoir « y a-t-il du web » n'est pas une question à part, c'est le succès ou
  // l'échec de ces requêtes.
  useEffect(() => {
    const lister = async (url: string): Promise<string[]> => {
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const doc = new DOMParser().parseFromString(await r.text(), "text/html");
      return trierParVersion(
        Array.from(doc.querySelectorAll("a"))
          .map((a) => a.getAttribute("href"))
          .filter((h): h is string => !!h && h.endsWith(".bin"))
      );
    };

    Promise.all([lister(URL_BINAIRES_CIBLE), lister(URL_BINAIRES_MASTER)])
      .then(([cibles, masters]) => {
        setFichiers(cibles);
        setFichiersMaster(masters);
        if (cibles.length === 0) {
          setErreurWeb("Aucun firmware de cible publié dans " + URL_BINAIRES_CIBLE);
        }
      })
      .catch((e) =>
        setErreurWeb(
          navigator.onLine
            ? "Serveur injoignable : " + e.message
            : "Pas de connexion internet"
        )
      );
  }, []);

  // Referme proprement si l'utilisateur quitte l'écran en cours de route.
  useEffect(() => {
    return () => {
      liaison.current?.fermer().catch(() => {});
    };
  }, []);

  /**
   * Demande l'état du stock et met l'affichage à jour.
   *
   * Retourne l'objet reçu : les setters React ne sont pas appliqués
   * immédiatement, et la suite de la séquence a besoin des valeurs tout de
   * suite (place libre, taille de bloc) — les relire dans l'état donnerait
   * celles du rendu précédent.
   */
  const interrogerMaster = async (l: LiaisonMaster): Promise<any> => {
    const stock = await l.attendre((o) => o.type === "stock", 3000);
    setStockVersion(stock.present ? stock.version : null);
    setStockTaille(stock.present ? stock.taille : 0);
    setPlaceLibre(stock.libre ?? 0);
    if (stock.bloc) setTailleBloc(stock.bloc);
    journaliser(
      stock.present
        ? `Image en stock : ${stock.version} (${Math.round(stock.taille / 1024)} Ko)`
        : "Aucune image en stock"
    );
    return stock;
  };

  const listerCibles = async (l: LiaisonMaster) => {
    await l.envoyer("CIBLES?");
    const recus = await l.collecter(
      (o) => o.type === "cible",
      (o) => o.type === "cibles" && o.fin === true,
      4000
    );
    setCibles(recus.map((o) => ({ mac: o.mac, nom: o.nom })));
    journaliser(`${recus.length} cible(s) connue(s) du Master`);
  };

  /**
   * Interroge le firmware en marche. Retourne sa version, ou null s'il ne
   * répond pas — cas d'un Master trop ancien (9600, ignore PING) comme d'un
   * Master en vrac. Dans les deux cas la réponse est la même : on le met à jour.
   */
  const lireVersionMaster = async (l: LiaisonMaster): Promise<string | null> => {
    // L'ouverture du port redémarre l'ESP32 (DTR) : le premier PING tombe
    // pendant son boot, alors que personne n'écoute encore. On réémet.
    await l.ouvrir(BAUD_MASTER);
    for (let essai = 0; essai < 6; essai++) {
      await l.envoyer("PING");
      try {
        const o = await l.attendre((x) => x.type === "master", 700);
        // Un Master dont esp_now_init() a échoué annonce une MAC vide : le
        // dire, plutôt que d'afficher un blanc qu'on prendrait pour un bug.
        setMacMaster(o.mac ? o.mac : null);
        journaliser(o.mac ? `Master ${o.mac}` : "Master sans MAC (ESP-NOW en échec ?)");
        return o.version ?? "?";
      } catch {
        /* pas encore prêt */
      }
    }
    // Rien à 115200 : un Master ≤ 0.6 parlerait à 9600. On regarde, uniquement
    // pour pouvoir le dire dans le journal — la suite est identique.
    await l.refermerPourAutreDebit();
    await l.ouvrir(BAUD_ANCIEN);
    try {
      await l.attendre((o) => o.type === "master" || (o.mac && o.chaine), 3000);
      journaliser("Master ancien détecté à 9600 bauds.");
    } catch {
      journaliser("Aucune réponse : Master muet, ou firmware hors service.");
    }
    return null;
  };

  /**
   * Réécrit l'application du Master par le bootloader ROM.
   *
   * Ne touche ni au bootloader ni à la table de partitions : la partition de
   * stockage et son contenu survivent donc à l'opération.
   */
  const flasherMaster = async (l: LiaisonMaster, fichier: string) => {
    const version = extraireVersion(fichier) ?? "?";
    journaliser(`Téléchargement du firmware Master ${version}…`);
    const rep = await fetch(`${URL_BINAIRES_MASTER}${fichier}`);
    if (!rep.ok) throw new Error(`téléchargement impossible (HTTP ${rep.status})`);
    const image = new Uint8Array(await rep.arrayBuffer());
    journaliser(`Firmware Master chargé : ${(image.length / 1024).toFixed(0)} Ko`);

    // esptool-js ouvre le port lui-même : il doit être libre, et il faut le
    // SAVOIR. fermer() lève si la fermeture échoue, plutôt que de laisser un
    // port à moitié ouvert produire une erreur incompréhensible plus loin.
    const port = l.portBrut;
    if (!port) throw new Error("port perdu");
    await l.fermer();

    const terminal: IEspLoaderTerminal = {
      clean: () => {},
      writeLine: (d: string) => journaliser(d),
      write: () => {},
    };

    const transport = new Transport(port, true);
    try {
      const { loader, puce } = await ouvrirChargeur(
        transport,
        terminal,
        journaliser
      );
      journaliser(`Puce détectée : ${puce}`);

      await loader.writeFlash({
        fileArray: [{ data: image, address: ADRESSE_APPLICATION }],
        flashMode: "dio",
        flashFreq: "40m",
        flashSize: "4MB",
        eraseAll: false,
        compress: true,
        reportProgress: (_i, ecrit, total) =>
          setProgMaster(Math.round((ecrit / total) * 100)),
      });
      await loader.after("hard_reset");
      journaliser(`✅ Master mis à jour en ${version}`);
    } finally {
      // disconnect() d'esptool-js boucle dans waitForUnlock(400) TANT QU'un
      // flux reste verrouillé, sans borne : un verrou jamais rendu y fige
      // l'application pour de bon, sans autre issue qu'un rechargement de page.
      // On borne l'attente et on continue — quitte à ce que le port reste
      // ouvert, cas que la reconnexion sait rattraper.
      await Promise.race([
        transport.disconnect().catch(() => {}),
        new Promise((r) => setTimeout(r, 3000)),
      ]);
    }
  };

  /**
   * Suite de la séquence, une fois le Master déjà flashé : connexion, lecture
   * du stock, chargement de l'image, liste des cibles.
   *
   * Séparée pour pouvoir être reprise seule. Après un flash, le Master est à
   * jour : rejouer la séquence complète le reflasherait pour rien, et surtout
   * l'utilisateur se retrouvait bloqué sur une erreur sans action possible.
   */
  const poursuivreApresMaster = async (
    l: LiaisonMaster,
    versionConnue: string | null = null
  ) => {
    // On ne réinterroge que si l'appelant ne connaît pas déjà la version : la
    // relire coûtait six PING inutiles, et surtout rouvrait un port ouvert.
    const v = versionConnue ?? (await lireVersionMaster(l));
    if (!v) throw new Error("le Master ne répond toujours pas");
    journaliser(`Master ${v} en ligne`);
    setVersionMaster(v);
    setConnecte(true);

    await l.envoyer("STOCK?");
    const stock = await interrogerMaster(l);

    const derniereCible =
      fichiers.length > 0 ? extraireVersion(fichiers[0]) : null;
    const stockAJour =
      !!derniereCible &&
      stock.present &&
      comparerVersions(stock.version, derniereCible) >= 0;

    setImageAvant(stock.present ? stock.version : "aucune");
    setImageVers(derniereCible);

    if (derniereCible && !stockAJour) {
      setPhase("chargement");
      setProgImage(0);
      journaliser(
        stock.present
          ? `Chargement de l'image de cible : ${stock.version} → ${derniereCible}`
          : `Chargement de l'image de cible ${derniereCible}`
      );
      await chargerImage(l, stock.libre ?? 0, stock.bloc ?? TAILLE_BLOC_DEFAUT);
      setEtat("ok");
    } else if (stockAJour) {
      journaliser("Image de cible déjà à jour, rien à charger.");
      setEtat("ok");
      setMessage(`Master prêt : image de cible ${derniereCible} embarquée.`);
    }

    await listerCibles(l);
  };

  /**
   * Reprise manuelle après un flash réussi dont la reconnexion a échoué.
   *
   * Repart d'un port neuf : le clic fournit le geste utilisateur qu'exige
   * requestPort(), ce qui permet de re-sélectionner le port même si le pont USB
   * s'est ré-énuméré sous un autre identifiant après le redémarrage.
   */
  const reprendre = async () => {
    setMessage("");
    setEtat("repos");
    setPhase("preparation");
    try {
      await liaison.current?.fermer().catch(() => {});
      liaison.current?.liberer();
      const l = new LiaisonMaster();
      liaison.current = l;
      journaliser("Reprise : sélectionnez à nouveau le port du Master.");
      await poursuivreApresMaster(l);
      setEtapeReprise(false);
    } catch (e: any) {
      setMessage(e?.message ?? String(e));
      setEtat("erreur");
      journaliser(`❌ ${e?.message ?? e}`);
    } finally {
      setPhase("repos");
    }
  };

  /**
   * Séquence complète, déclenchée par un seul bouton. Tout Master qui arrive
   * ici repart à jour : pas d'étape préalable à cliquer, pas de message qui
   * renvoie ailleurs.
   */
  // L'événement sert au Maj + clic : il lève le filtre sur les puces USB des
  // Masters (cf. portSerie.ts).
  const preparerMaster = async (ev?: { shiftKey?: boolean }) => {
    setMessage("");
    setJournal([]);
    setEtat("repos");
    setPhase("preparation");
    setMasterMisAJour(false);
    setEtapeReprise(false);
    setProgMaster(0);
    setProgImage(0);
    setMasterAvant(null);
    setMasterVers(null);
    setImageAvant(null);
    setImageVers(null);

    const l = new LiaisonMaster();
    l.sansFiltre = ev?.shiftKey === true;
    liaison.current = l;

    try {
      if (l.sansFiltre) { journaliser("Maj + clic : tous les ports série sont listés."); }
      journaliser("Lecture de la version du Master…");
      let v = await lireVersionMaster(l);
      if (v) journaliser(`Master ${v} détecté à ${l.baud} bauds`);
      setMasterAvant(v ?? "muet");

      const derniereMaster =
        fichiersMaster.length > 0 ? extraireVersion(fichiersMaster[0]) : null;

      // Pas de réponse, ou version en retard → on met à jour, point.
      const aMettreAJour =
        !!derniereMaster && (!v || comparerVersions(v, derniereMaster) < 0);

      if (aMettreAJour) {
        setMasterVers(derniereMaster);
        journaliser(
          v
            ? `Mise à jour du Master : ${v} → ${derniereMaster}`
            : `Mise à jour du Master vers ${derniereMaster}`
        );
        await flasherMaster(l, fichiersMaster[0]);
        setMasterMisAJour(true);

        // Le Master redémarre, et le pont USB peut se ré-énumérer : on laisse
        // le temps, puis on réessaie. Une seule tentative immédiate tombait
        // trop tôt et donnait une erreur sans recours.
        journaliser("Reconnexion après redémarrage…");
        v = null;
        for (let essai = 1; essai <= 3 && !v; essai++) {
          await new Promise((r) => setTimeout(r, 1500));
          try {
            await l.fermer();
          } catch {
            /* pas grave, on retente d'ouvrir */
          }
          try {
            v = await lireVersionMaster(l);
          } catch (e: any) {
            journaliser(`Tentative ${essai}/3 : ${e?.message ?? e}`);
          }
        }

        if (!v) {
          // Le Master EST à jour : il ne faut surtout pas le reflasher. On
          // propose de reprendre à l'étape suivante plutôt que de laisser
          // l'utilisateur devant une erreur sans issue.
          setEtapeReprise(true);
          throw new Error(
            "Master mis à jour, mais le port ne se rouvre pas. " +
              "Débranchez et rebranchez l'USB, puis cliquez sur « Reprendre »."
          );
        }
        journaliser(`Master ${v} en ligne`);
      } else if (!derniereMaster) {
        journaliser("Aucun firmware Master publié : mise à jour impossible.");
        if (!v) throw new Error("Master muet et aucun firmware disponible");
      }

      // Enchaîné sans rien demander : un Master qui passe ici repart avec le
      // firmware de cible du jour. La version est déjà connue — flashé ou non,
      // on vient de la lire.
      await poursuivreApresMaster(l, v);
      setPhase("repos");
    } catch (e: any) {
      setMessage(e?.message ?? String(e));
      setEtat("erreur");
      setPhase("repos");
      journaliser(`❌ ${e?.message ?? e}`);
      // Un chargement interrompu laisserait le Master bloqué en réception.
      try {
        await liaison.current?.envoyer("ABORTLOAD");
      } catch {
        /* liaison déjà perdue */
      }
    }
  };

  const deconnecter = async () => {
    await liaison.current?.fermer().catch(() => {});
    liaison.current = null;
    setConnecte(false);
    setVersionMaster(null);
    setStockVersion(null);
    setCibles([]);
  };

  /**
   * Transfère l'image de cible vers le Master. Lève en cas d'échec.
   *
   * `libre` et `bloc` sont passés en paramètre plutôt que lus dans l'état :
   * appelée dans la foulée d'interrogerMaster(), la fonction verrait sinon les
   * valeurs du rendu précédent.
   */
  const chargerImage = async (l: LiaisonMaster, libre: number, bloc: number) => {
    const fichier = fichiers[0];
    if (!fichier) throw new Error("aucun firmware de cible publié");
    const version = extraireVersion(fichier) ?? "?";

    // L'image est récupérée et compressée AVANT de toucher au Master : si le
    // réseau lâche, il n'a pas encore effacé son stock précédent.
    journaliser(`Téléchargement de ${fichier}…`);
    const rep = await fetch(`${URL_BINAIRES_CIBLE}${fichier}`);
    if (!rep.ok) throw new Error(`téléchargement impossible (HTTP ${rep.status})`);
    const brut = new Uint8Array(await rep.arrayBuffer());
    journaliser(`Image brute : ${brut.length} octets`);

    // deploy.js publie le binaire brut, qui ne tient PAS dans la partition de
    // stockage. C'est la compression qui rend l'opération possible, et c'est
    // aussi la forme utile pour la future diffusion ESP-NOW : la cible sait
    // inflater du zlib à la volée pendant son OTA (flashz).
    const image = await compresserZlib(brut);
    const ratio = Math.round((100 * image.length) / brut.length);
    journaliser(
      `Compressé en zlib : ${image.length} octets (${ratio} % du brut)`
    );

    if (libre > 0 && image.length > libre) {
      throw new Error(
        `image compressée de ${image.length} octets pour ${libre} octets ` +
          `libres sur le Master. Même compressée elle ne tient pas : la ` +
          `partition de stockage doit être agrandie.`
      );
    }
    if (libre > 0) {
      journaliser(`Place restante après écriture : ${libre - image.length} octets`);
    }

    const somme = crc32(image);
    setCrcLocal(somme);
    setCrcMaster(null);
    journaliser(`CRC32 calculé localement : ${enHexa(somme)}`);

    // La taille décompressée voyage jusqu'à la cible : elle seule permet de
    // détecter une image tronquée AVANT de basculer la partition de démarrage.
    await l.envoyer(`LOAD ${version} ${image.length} ${somme} ${brut.length}`);
    const pret = await l.attendre((o) => o.type === "load", 8000);
    if (!pret.pret) throw new Error(pret.raison ?? "Master non prêt");

    const taille = pret.bloc ?? bloc;
    const nbBlocs = Math.ceil(image.length / taille);
    journaliser(`Transfert en ${nbBlocs} blocs de ${taille} octets…`);

    for (let index = 0; index < nbBlocs; index++) {
      const debut = index * taille;
      const morceau = image.subarray(debut, Math.min(debut + taille, image.length));
      await l.envoyer(`DATA ${index} ${enBase64(morceau)}`);
      // Acquittement bloc par bloc : c'est le seul contrôle de flux possible,
      // Web Serial rendant la main avant que l'ESP32 n'ait lu.
      const ack = await l.attendre(
        (o) => o.type === "data" && o.index === index,
        10000
      );
      if (!ack.ok) throw new Error(`bloc ${index} refusé : ${ack.raison}`);
      setProgImage(Math.round(((index + 1) / nbBlocs) * 100));
    }

    await l.envoyer("ENDLOAD");
    const fin = await l.attendre((o) => o.type === "load" && o.fini, 20000);

    // Le CRC recalculé par le Master sur ce qu'il a réellement écrit. C'est la
    // seule preuve que l'image arrivée au bout du fil est celle qu'on a
    // téléchargée : on l'affiche, on ne se contente pas du verdict.
    if (typeof fin.crc === "number") {
      setCrcMaster(fin.crc >>> 0);
      journaliser(`CRC32 recalculé par le Master : ${enHexa(fin.crc >>> 0)}`);
    }
    if (!fin.valide) throw new Error(fin.raison || "vérification en échec");

    journaliser(`✅ CRC concordant — image ${version} en stock`);
    setMessage(`Le Master embarque maintenant la version ${version}.`);
    setProgImage(100);

    await l.envoyer("STOCK?");
    await interrogerMaster(l);
  };

  /**
   * Pousse l'image en stock vers UNE cible, par ESP-NOW.
   *
   * Le Master fait tout le travail : on lui donne une adresse, il propose,
   * transfère bloc par bloc et rend compte. Le navigateur ne fait que suivre —
   * la boucle d'acquittement se joue à la milliseconde, elle ne peut pas
   * traverser la liaison série à chaque paquet.
   *
   * La cible redémarre d'elle-même en fin de mise à jour : elle disparaîtra
   * quelques secondes de la liste avant d'y revenir avec sa nouvelle version.
   */
  const flasherCible = async (mac: string) => {
    const l = liaison.current;
    if (!l) return;

    setPhase("flashCible");
    setCibleEnCours(mac);
    setProgCible(0);
    setMessage("");
    setEtat("encours");

    const surLigne = (o: any) => {
      if (o.type !== "flash") return;
      if (typeof o.pct === "number") setProgCible(o.pct);
      if (o.etape) journaliser(`[${mac}] ${o.etape} ${o.detail ?? ""}`);
    };
    l.abonner(surLigne);

    try {
      journaliser(`Mise à jour ESP-NOW de ${mac}…`);
      await l.envoyer(`FLASH ${mac}`);
      const fin = await l.attendre(
        (o) => o.type === "flash" && o.fini === true,
        DELAI_FLASH_CIBLE
      );
      // Les octets réellement écrits par la cible après décompression, contre
      // ceux attendus. C'est la mesure qui distingue un transfert perdu d'une
      // décompression incomplète — sans elle, les deux se présentent
      // identiquement : « la cible redémarre sur son ancienne version ».
      if (typeof fin.ecrits === "number") {
        journaliser(
          `[${mac}] écrits ${fin.ecrits} / attendus ${fin.attendus ?? "?"}` +
            (fin.attendus && fin.ecrits !== fin.attendus
              ? ` — MANQUE ${fin.attendus - fin.ecrits} octets`
              : "")
        );
      }
      if (!fin.ok) {
        const detail =
          fin.attendus && fin.ecrits !== fin.attendus
            ? `image incomplète : ${fin.ecrits} octets écrits pour ${fin.attendus} attendus`
            : fin.raison ?? "échec du transfert";
        throw new Error(detail);
      }

      setProgCible(100);
      setEtat("ok");
      setMessage(`Cible ${mac} mise à jour en ${fin.version}. Elle redémarre.`);
      journaliser(`✅ ${mac} → ${fin.version}, redémarrage de la cible`);

      // Elle se re-signalera d'elle-même, mais laisser l'utilisateur devant une
      // liste périmée donnerait l'impression que rien ne s'est passé.
      await new Promise((r) => setTimeout(r, 4000));
      await listerCibles(l);
    } catch (e: any) {
      const detail = e?.message ?? String(e);
      setEtat("erreur");
      setMessage(`Cible ${mac} : ${detail}`);
      journaliser(`❌ ${mac} : ${detail}`);
    } finally {
      l.desabonner(surLigne);
      setCibleEnCours(null);
      setPhase("repos");
    }
  };

  /** Rechargement forcé, quand le stock est déjà à jour. */
  const rechargerImage = async () => {
    const l = liaison.current;
    if (!l) return;
    setEtat("encours");
    setPhase("chargement");
    setProgImage(0);
    setMessage("");
    try {
      await chargerImage(l, placeLibre, tailleBloc);
      setEtat("ok");
    } catch (e: any) {
      const detail = e?.message ?? String(e);
      setEtat("erreur");
      setMessage(detail);
      journaliser(`❌ ${detail}`);
      // Sans ça le Master reste bloqué en réception jusqu'à son prochain
      // redémarrage, et cesse d'émettre ses résultats de course.
      try {
        await l.envoyer("ABORTLOAD");
      } catch {
        /* liaison déjà perdue */
      }
    } finally {
      setPhase("repos");
    }
  };

  const derniere = fichiers.length > 0 ? extraireVersion(fichiers[0]) : null;
  const derniereMaster =
    fichiersMaster.length > 0 ? extraireVersion(fichiersMaster[0]) : null;
  const aJour =
    !!derniere && !!stockVersion && comparerVersions(stockVersion, derniere) >= 0;
  const occupe = phase !== "repos";

  const libelleBouton =
    phase === "preparation"
      ? "Mise à jour du Master…"
      : phase === "chargement"
      ? "Chargement de l'image…"
      : connecte
      ? "Recommencer"
      : "Préparer le Master";

  return (
    <IonContent class="ion-padding">
      <IonCard>
        <IonCardHeader>
          <IonCardTitle>Préparer un Master</IonCardTitle>
          <IonCardSubtitle>
            Branchez le Master en USB et lancez. Il est mis à jour puis chargé
            avec le dernier firmware de cible, sans autre intervention. La mise à
            jour passe par le bootloader de l'ESP32 : elle fonctionne même sur un
            Master trop ancien pour répondre, ou dont le firmware est en vrac.
          </IonCardSubtitle>
        </IonCardHeader>

        <IonCardContent>
          {!serieDisponible && (
            <IonText color="danger">
              <p>
                Ce navigateur n'expose pas le port série. Utilisez le BZØØK
                Booster, ou Chrome / Edge sur ordinateur.
              </p>
            </IonText>
          )}

          {erreurWeb && (
            <IonText color="danger">
              <p>{erreurWeb}</p>
            </IonText>
          )}

          <IonGrid>
            <IonRow>
              <IonCol>
                <IonCardSubtitle>Firmware Master publié</IonCardSubtitle>
                <IonText>
                  <p className="ota-target-version">{derniereMaster ?? "—"}</p>
                </IonText>
              </IonCol>
              <IonCol>
                <IonCardSubtitle>Firmware cible publié</IonCardSubtitle>
                <IonText>
                  <p className="ota-target-version">{derniere ?? "—"}</p>
                </IonText>
              </IonCol>
            </IonRow>
          </IonGrid>

          <IonButton
            onClick={preparerMaster}
            disabled={!serieDisponible || occupe || !!erreurWeb}
          >
            {libelleBouton}
          </IonButton>
          <IonButton color="medium" onClick={onFermer} disabled={occupe}>
            Retour
          </IonButton>

          {/* Deux avancements distincts : le flash du Master, puis le transfert
              de l'image. Chacun annonce d'où il part et où il va. */}
          {masterAvant && (
            <div className="parc-etape">
              <IonCardSubtitle>
                Firmware Master&nbsp;: {masterAvant}
                {masterVers ? ` → ${masterVers}` : " (déjà à jour)"}
              </IonCardSubtitle>
              <ProgressBar
                completed={masterVers ? progMaster : 100}
                labelAlignment="center"
                bgColor="#3880ff"
              />
            </div>
          )}

          {imageAvant && (
            <div className="parc-etape">
              <IonCardSubtitle>
                Image de cible&nbsp;: {imageAvant}
                {imageVers && imageVers !== imageAvant
                  ? ` → ${imageVers}`
                  : " (déjà à jour)"}
              </IonCardSubtitle>
              <ProgressBar
                completed={
                  imageVers && imageVers !== imageAvant ? progImage : 100
                }
                labelAlignment="center"
                bgColor="#2dd36f"
              />
            </div>
          )}

          {/* Vérification d'intégrité, montrée et non seulement conclue. */}
          {crcLocal !== null && (
            <div className="parc-etape">
              <IonCardSubtitle>Vérification CRC32</IonCardSubtitle>
              <IonGrid>
                <IonRow>
                  <IonCol>
                    <IonText>
                      <p>
                        Calculé ici
                        <br />
                        <code>{enHexa(crcLocal)}</code>
                      </p>
                    </IonText>
                  </IonCol>
                  <IonCol>
                    <IonText>
                      <p>
                        Recalculé par le Master
                        <br />
                        <code>
                          {crcMaster !== null ? enHexa(crcMaster) : "…"}
                        </code>
                      </p>
                    </IonText>
                  </IonCol>
                </IonRow>
              </IonGrid>
              {crcMaster !== null && (
                <IonText color={crcMaster === crcLocal ? "success" : "danger"}>
                  <p>
                    {crcMaster === crcLocal
                      ? "✅ Empreintes identiques : l'image écrite est bien celle qui a été téléchargée."
                      : "❌ Empreintes différentes : l'image reçue par le Master n'est pas celle envoyée. Rien n'a été mis en stock."}
                  </p>
                </IonText>
              )}
            </div>
          )}

          {message && (
            <IonText color={etat === "erreur" ? "danger" : "success"}>
              <p>{message}</p>
            </IonText>
          )}

          {/* Le Master est flashé, seule la reconnexion a échoué. On guide au
              lieu de laisser une erreur sans issue — et surtout on ne repart
              pas du début, ce qui le reflasherait pour rien. */}
          {etapeReprise && (
            <IonCard color="warning">
              <IonCardContent>
                <IonText>
                  <p>
                    <strong>Le Master a bien été mis à jour.</strong> Seule la
                    reconnexion au port a échoué — c'est fréquent quand le pont
                    USB se ré-énumère après le redémarrage.
                  </p>
                  <p>
                    1. Débranchez puis rebranchez le câble USB.
                    <br />
                    2. Cliquez sur « Reprendre » et re-sélectionnez le port.
                  </p>
                  <p>
                    Le firmware du Master ne sera pas réécrit : on repart
                    directement au chargement de l'image de cible.
                  </p>
                </IonText>
                <IonButton onClick={reprendre} disabled={occupe}>
                  Reprendre
                </IonButton>
              </IonCardContent>
            </IonCard>
          )}

          {occupe && (
            <IonText color="warning">
              <p>
                Ne débranchez pas le Master. Une coupure n'abîme rien
                définitivement, mais il faudra recommencer.
              </p>
            </IonText>
          )}
        </IonCardContent>
      </IonCard>

      {connecte && (
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>État du Master</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonGrid>
              <IonRow>
                <IonCol>
                  <IonCardSubtitle>Firmware</IonCardSubtitle>
                  <IonText>
                    <p className="ota-target-version">
                      {versionMaster ?? "?"}
                      {masterMisAJour ? " (mis à jour)" : ""}
                    </p>
                  </IonText>
                </IonCol>
                <IonCol>
                  <IonCardSubtitle>Adresse MAC</IonCardSubtitle>
                  <IonText>
                    <p className="ota-target-version">
                      {macMaster ?? "non annoncée"}
                    </p>
                  </IonText>
                </IonCol>
                <IonCol>
                  <IonCardSubtitle>Image de cible embarquée</IonCardSubtitle>
                  <IonText>
                    <p className="ota-target-version">
                      {stockVersion
                        ? `${stockVersion} (${Math.round(stockTaille / 1024)} Ko)`
                        : "aucune"}
                    </p>
                  </IonText>
                </IonCol>
              </IonRow>
            </IonGrid>

            {aJour && (
              <IonText color="success">
                <p>Le Master embarque la dernière version publiée.</p>
              </IonText>
            )}

            <IonButton color="medium" onClick={rechargerImage} disabled={occupe}>
              Recharger l'image
            </IonButton>
            <IonButton color="medium" onClick={deconnecter} disabled={occupe}>
              Déconnecter
            </IonButton>
          </IonCardContent>
        </IonCard>
      )}

      {/* --------------------------------------------------- étape 4 */}
      {connecte && (
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Cibles vues par le Master ({cibles.length})</IonCardTitle>
            <IonCardSubtitle>
              La version de chaque cible est lue dans le nom qu'elle annonce.
            </IonCardSubtitle>
          </IonCardHeader>
          <IonCardContent>
            {cibles.length === 0 ? (
              <IonText>
                <p>
                  Aucune cible pour l'instant. Elles se signalent d'elles-mêmes
                  toutes les 8 secondes une fois allumées.
                </p>
              </IonText>
            ) : (
              <IonList>
                {cibles.map((c) => {
                  const v = versionDeCible(c.nom);
                  // Ce que le Master peut réellement donner, c'est ce qu'il a en
                  // stock — pas ce qui est publié sur le serveur.
                  const enRetard =
                    !!v && !!stockVersion && comparerVersions(v, stockVersion) < 0;
                  // Une cible antérieure à la 5.14 ignore les messages OTA :
                  // proposer le bouton ne ferait qu'attendre un délai pour rien.
                  const capable =
                    !!v && comparerVersions(v, VERSION_MIN_OTA_ESPNOW) >= 0;
                  const cetteCible = cibleEnCours === c.mac;

                  return (
                    <IonItem key={c.mac}>
                      <IonLabel>
                        <h2>{c.nom || "(sans nom)"}</h2>
                        <p>{c.mac}</p>
                        {cetteCible && (
                          <ProgressBar
                            completed={progCible}
                            labelAlignment="center"
                            bgColor="#ffc409"
                          />
                        )}
                        {!capable && v && (
                          <p className="parc-note">
                            Trop ancienne pour l'ESP-NOW — un dernier passage par
                            le BLE est nécessaire.
                          </p>
                        )}
                      </IonLabel>

                      <IonBadge slot="end" color={enRetard ? "warning" : "success"}>
                        {v ?? "?"}
                      </IonBadge>

                      <IonButton
                        slot="end"
                        size="small"
                        color="warning"
                        disabled={
                          occupe || !capable || !enRetard || !stockVersion
                        }
                        onClick={() => flasherCible(c.mac)}
                      >
                        {cetteCible ? `${progCible} %` : "MAJ"}
                      </IonButton>
                    </IonItem>
                  );
                })}
              </IonList>
            )}
            <IonButton
              color="medium"
              onClick={() => liaison.current && listerCibles(liaison.current)}
              disabled={occupe}
            >
              Rafraîchir
            </IonButton>
          </IonCardContent>
        </IonCard>
      )}

      {journal.length > 0 && (
        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Journal</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonGrid>
              <IonRow>
                <pre className="ota-journal">
                  {journal.join("\n")}
                  <div ref={finJournal} />
                </pre>
              </IonRow>
            </IonGrid>
          </IonCardContent>
        </IonCard>
      )}
    </IonContent>
  );
};

export default ParcCibles;
